import { memo, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Layout, PageTitle, AddCardIcon } from "lola-framework-ui-test";

import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { RequireActiveClerkSession } from "../hooks/useRequireActiveClerkSession";
import {
  PayoutCardValidationError,
  preValidatePayoutCard,
} from "../utils/pre-validate-payout-card";
import { paymentType } from "../interfaces/payment";
import { isDeadLinkError } from "../services/api";
import { useClaimLink } from "../hooks/useClaimLink";
import { useClaimableGuard } from "../hooks/useClaimableGuard";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import type { CardStepFields } from "./AddCardPage";
import { getEvents, safeCapture } from "../analytics";

export interface AddCardFullState {
  card?: CardStepFields;
}

let isPreValidateInFlight = false;

const MIN_PROCESSING_VISIBLE_MS = 2000;

const holdMinimumVisible = (visibleSince: number) => {
  const remaining = MIN_PROCESSING_VISIBLE_MS - (Date.now() - visibleSince);
  if (remaining <= 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, remaining);
  });
};

/**
 * Add-card: VGS tokenize + CVS destination pre-validate (dry-run only).
 * Register + rail provision happen on Transfer confirm via preparePayoutRail.
 */
export const AddCardProcessingPage = memo(() => (
  <RequireActiveClerkSession resume={{ kind: "transfer" }} requireOccupant>
    <AddCardProcessingPageContent />
  </RequireActiveClerkSession>
));

const AddCardProcessingPageContent = memo(() => {
  useClaimLink();
  useClaimableGuard();
  const { t } = useTranslation();
  const { theme, setPaymentState, setPayoutInstrument } = useStore();
  const { session } = useSessionStore();
  const navigate = useNavigate();
  const { redirectTo } = useRouteWithToken();
  const location = useLocation();
  const colorConfig = theme.whiteView;
  const { dropzoneColors, backgroundIcon } = colorConfig;

  const formData = location.state as AddCardFullState | undefined;
  const visibleSinceRef = useRef(Date.now());

  useEffect(() => {
    safeCapture(getEvents().ADD_CARD_PROCESSING_VIEW);
  }, []);

  useEffect(() => {
    if (!formData?.card) {
      redirectTo(APP_ROUTES.ADD_CARD_PAGE, { replace: true });
      return;
    }

    if (isPreValidateInFlight) return;
    isPreValidateInFlight = true;

    const { card } = formData;

    const runPreValidate = async () => {
      try {
        const linkId = session.data?.linkId;
        if (!linkId) throw new Error("No linkId found in session");

        const config = session.data?.config;
        const sendCommitment = config?.sendCommitment;
        const source =
          (sendCommitment?.sourceCountry ?? config?.sourceCountry ?? "US")
            .trim()
            .toUpperCase() || "US";
        const destination =
          (
            sendCommitment?.destinationCountry ??
            config?.destinationCountry ??
            "MX"
          )
            .trim()
            .toUpperCase() || "MX";

        const cleanedNumber = card.cardNumber.replace(/\s/g, "");
        const holder = card.cardHolderName.trim();

        const pre = await preValidatePayoutCard(linkId, {
          pan: cleanedNumber,
          expiry: card.expiry,
          cardholderName: holder || undefined,
          sourceCountry: source,
          destinationCountry: destination,
        });

        const displayName = `${(pre.brand || "").toUpperCase()}-${pre.last4}`;

        setPayoutInstrument({
          source: "new",
          cardPan: cleanedNumber,
          cardExpiry: card.expiry,
          cardholderName: holder || undefined,
          cvsValidationId: pre.validationId,
          numberToken: pre.numberToken,
          brand: pre.brand,
          last4: pre.last4,
          validatedFor: {
            sourceCountry: source,
            destinationCountry: destination,
          },
        });

        setPaymentState({
          payment: {
            cardId: null,
            type: paymentType.CARD,
            brand: displayName,
          },
        });

        await holdMinimumVisible(visibleSinceRef.current);
        redirectTo(APP_ROUTES.TRANSFER_PAGE, { replace: true });
      } catch (error) {
        console.error("Failed to pre-validate destination card:", error);

        // Link expired / revoked: the dead-link handler already routed to the
        // screen that explains why the link stopped working.
        if (isDeadLinkError(error)) {
          return;
        }

        const isRateLimited =
          axios.isAxiosError(error) && error.response?.status === 429;

        const validationCode =
          error instanceof PayoutCardValidationError ? error.code : undefined;

        navigate(APP_ROUTES.ERROR_PAGE, {
          replace: true,
          state: isRateLimited
            ? {
                title: t("error.rateLimitTitle"),
                subTitle: t("error.rateLimitSubtitle"),
                message: t("error.rateLimitMessage"),
                btnText: t("card.tryAgain"),
                isOut: false,
                pageUrl: APP_ROUTES.ADD_CARD_PAGE,
                returnTo: APP_ROUTES.TRANSFER_PAGE,
              }
            : {
                title: t("card.registrationFailedTitle"),
                subTitle: t("card.registrationFailedSubtitle"),
                message:
                  validationCode === "CARD_UNDER_REVIEW"
                    ? t("card.underReview")
                    : validationCode === "CARD_PUSH_NOT_SUPPORTED"
                      ? t("card.pushNotSupported")
                      : validationCode === "CARD_CORRIDOR_CONTEXT_REQUIRED"
                        ? t("payout.card.corridorContextRequired")
                        : validationCode === "CARD_VALIDATION_FAILED"
                          ? t("card.validationFailed")
                          : validationCode === "VGS_CONFIG_UNAVAILABLE"
                            ? t("card.vgsUnavailable")
                            : t("card.registrationFailed"),
                btnText: t("card.tryAgain"),
                isOut: false,
                pageUrl: APP_ROUTES.ADD_CARD_PAGE,
                returnTo: APP_ROUTES.TRANSFER_PAGE,
              },
        });
      } finally {
        isPreValidateInFlight = false;
      }
    };

    runPreValidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark
            showSpinner
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundIcon}
          >
            <AddCardIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("card.addingCard")}
              highlightColor={TITLE_COLOR}
              secudnary={t("card.pleaseWait")}
              secudnaryColor={TITLE_COLOR}
            />
          </section>
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});
